import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Compass,
  Cpu,
  ShieldCheck,
  Award,
  Users,
  ChevronRight,
  Sparkles,
  Phone,
} from "lucide-react";

const TABS = [
  { key: "journey", label: "Unified Journey", Icon: Compass },
  { key: "arch", label: "Technology", Icon: Cpu },
  { key: "limits", label: "Limitations", Icon: ShieldCheck },
  { key: "validation", label: "Expert Validation", Icon: Award },
  { key: "impact", label: "Real-World Impact", Icon: Users },
];

export default function Impact() {
  const [tab, setTab] = useState("journey");
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tab]);

  return (
    <div className="min-h-screen bg-[#05050A] text-white pb-24">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(45% 30% at 10% 0%, rgba(0,229,255,0.14) 0%, transparent 60%), radial-gradient(45% 30% at 95% 20%, rgba(167,139,250,0.15) 0%, transparent 60%)",
        }}
      />
      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="impact-back-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <a
            href="tel:108"
            className="inline-flex items-center gap-1.5 rounded-full bg-red-500 text-black text-xs font-semibold px-3 py-1.5"
          >
            <Phone className="w-3.5 h-3.5" />
            Emergency 108
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-12">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-cyan-300/80">
            Chapter 15 · The Project
          </div>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-semibold leading-[1.02]">
            From preventive awareness to{" "}
            <span className="italic text-cyan-300">personalized rehabilitation.</span>
          </h1>
          <p className="mt-5 text-zinc-300 max-w-2xl text-lg leading-relaxed">
            An AI-assisted platform designed to support preventive health
            awareness and personalized cognitive rehabilitation through
            assessment, adaptive training and longitudinal performance
            tracking. This page shows the &quot;how&quot; and the &quot;what&quot; — the
            technology, its limits and its intended impact.
          </p>
        </div>

        <div className="mt-8 border-b border-white/5 overflow-x-auto no-scrollbar">
          <div className="flex gap-1 min-w-max">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`impact-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2.5 text-sm inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                  tab === t.key
                    ? "border-cyan-400 text-white"
                    : "border-transparent text-zinc-400 hover:text-white"
                }`}
              >
                <t.Icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10">
          {tab === "journey" && <Journey />}
          {tab === "arch" && <Architecture />}
          {tab === "limits" && <Limitations />}
          {tab === "validation" && <Validation />}
          {tab === "impact" && <RealImpact />}
        </div>
      </div>
    </div>
  );
}

const STAGES = [
  { key: "prevent", label: "Prevent", body: "Preventive-health awareness. AI Health Score + Twin + diet planner." },
  { key: "assess", label: "Assess", body: "Baseline cognitive assessment across 5 domains — memory, attention, speed, problem-solving, executive function." },
  { key: "personalize", label: "Personalize", body: "A rule-based scheduler builds each session from the user's task performance." },
  { key: "rehabilitate", label: "Rehabilitate", body: "Adaptive exercises change difficulty based on measured performance — never on invented medical rules." },
  { key: "measure", label: "Measure", body: "Every session records accuracy, reaction time, consistency and errors." },
  { key: "track", label: "Track", body: "Longitudinal trends over weeks — compared only to the user's own past self." },
  { key: "support", label: "Support Independence", body: "Caregiver & professional views (with consent) help the ecosystem around the user." },
];

const Journey = () => (
  <div>
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
        One connected experience
      </div>
      <h2 className="mt-2 font-display text-3xl font-semibold">
        PREVENT → ASSESS → PERSONALIZE → REHABILITATE → MEASURE → TRACK → SUPPORT
      </h2>
      <p className="mt-3 text-sm text-zinc-400 max-w-2xl">
        SilentScan AI (preventive health) and NeuroRehab AI (cognitive
        rehabilitation support) are not two apps. They share the same profile,
        the same measurement language and the same responsible-AI principles.
      </p>
    </div>
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {STAGES.map((s, i) => (
        <motion.div
          key={s.key}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: i * 0.05 }}
          className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
        >
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
            Stage {i + 1}
          </div>
          <div className="mt-1 font-display text-xl font-semibold text-white">{s.label}</div>
          <p className="mt-2 text-sm text-zinc-300 leading-relaxed">{s.body}</p>
        </motion.div>
      ))}
    </div>
  </div>
);

const ARCH_STEPS = [
  { t: "User input", b: "Health questionnaire, cognitive assessment, wearable data (with consent)." },
  { t: "Assessment engine", b: "Deterministic scoring for 6 twin dimensions and 5 cognitive domains." },
  { t: "Performance analysis", b: "Accuracy, reaction time, error patterns and consistency across sessions." },
  { t: "Adaptive engine", b: "Rule-based scheduler selects the next exercise + difficulty from a fixed, safe library. No LLM invents treatment." },
  { t: "Personalized exercise", b: "Interactive tasks: object recall, target detection, symbol matching, pattern completion, rule switching." },
  { t: "Progress database", b: "Local-first storage. Backend sync only for the AI Assistant, Community and shared Twin." },
  { t: "Dashboards", b: "User · Caregiver (opt-in, revocable) · Professional review (prototype)." },
];

const Architecture = () => (
  <div>
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
      <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
        Architecture
      </div>
      <h2 className="mt-2 font-display text-3xl font-semibold">
        Predictable. Explainable. Safe.
      </h2>
      <p className="mt-3 text-sm text-zinc-400 max-w-2xl">
        The AI language model is used ONLY for education and explanation.
        Exercise selection, difficulty and progression are entirely
        rule-based — so nothing dangerous or fabricated can slip in.
      </p>
    </div>
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
      {ARCH_STEPS.map((s, i) => (
        <div key={s.t} className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
            {`${String(i + 1).padStart(2, "0")} · Component`}
          </div>
          <h3 className="mt-2 font-display text-lg font-semibold text-white">{s.t}</h3>
          <p className="mt-2 text-sm text-zinc-300 leading-relaxed">{s.b}</p>
        </div>
      ))}
    </div>
  </div>
);

const LIMITS = [
  "SilentScan AI cannot diagnose neurological diseases or any medical condition.",
  "NeuroRehab AI cannot guarantee recovery. Cognitive-task performance in a simulated exercise is not automatically equivalent to real-world cognitive recovery.",
  "The platform cannot replace a doctor, neurologist, psychologist, occupational therapist or rehabilitation professional.",
  "The platform requires further peer-reviewed research and clinical validation before it can be used as a clinical tool.",
  "Baseline results are affected by fatigue, mood, environment and prior familiarity with digital devices. A single session is not a reliable indicator.",
  "The educational AI assistant may occasionally produce inaccurate information. Always confirm important decisions with a qualified clinician.",
];

const Limitations = () => (
  <div>
    <div className="rounded-3xl border border-amber-500/30 bg-amber-500/5 p-8">
      <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
        Transparent about what we&apos;re not
      </div>
      <h2 className="mt-2 font-display text-3xl font-semibold">
        What SilentScan AI cannot do
      </h2>
      <p className="mt-3 text-sm text-amber-100/90 max-w-2xl leading-relaxed">
        A responsible health technology is honest about its boundaries. These
        limits are as important as the features.
      </p>
    </div>
    <div className="mt-6 flex flex-col gap-2">
      {LIMITS.map((l, i) => (
        <div
          key={i}
          className="rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 flex items-start gap-3"
        >
          <div className="w-6 h-6 rounded-full bg-amber-400/20 border border-amber-400/40 text-amber-200 text-xs flex items-center justify-center font-semibold flex-shrink-0">
            {i + 1}
          </div>
          <p className="text-sm text-zinc-200 leading-relaxed">{l}</p>
        </div>
      ))}
    </div>
  </div>
);

const VALIDATION_ROWS = [
  { label: "Rehabilitation-professional review", status: "Pending" },
  { label: "Exercise safety review", status: "Pending" },
  { label: "Usability review with target users", status: "Pending" },
  { label: "IRB-approved pilot study", status: "Not started" },
  { label: "Peer-reviewed publication", status: "Not started" },
];

const Validation = () => (
  <div>
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
      <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
        Expert validation
      </div>
      <h2 className="mt-2 font-display text-3xl font-semibold">
        Reviewed by real professionals — not marketing.
      </h2>
      <p className="mt-3 text-sm text-zinc-400 max-w-2xl leading-relaxed">
        This platform has NOT yet been clinically validated. We publish
        placeholders below so we can transparently update this page as real
        reviews arrive. We do not display fake certificates, fake doctors or
        fake testimonials.
      </p>
    </div>
    <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.03] overflow-hidden">
      {VALIDATION_ROWS.map((r, i) => (
        <div
          key={r.label}
          className={`flex items-center justify-between px-6 py-4 ${
            i < VALIDATION_ROWS.length - 1 ? "border-b border-white/5" : ""
          }`}
        >
          <div className="text-sm text-white">{r.label}</div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
            {r.status}
          </div>
        </div>
      ))}
    </div>
    <div className="mt-4 rounded-2xl border border-cyan-400/30 bg-cyan-400/5 p-4 text-xs text-cyan-100 leading-relaxed flex items-start gap-2">
      <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
      Are you a clinician or researcher interested in reviewing this project?
      Reach out to <a className="underline" href="mailto:nutriplate07@gmail.com">nutriplate07@gmail.com</a>.
    </div>
  </div>
);

const RealImpact = () => (
  <div>
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
      <div className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold">
        Real-world impact
      </div>
      <h2 className="mt-2 font-display text-3xl font-semibold">
        Designed for real people, real problems.
      </h2>
      <p className="mt-3 text-sm text-zinc-400 max-w-2xl leading-relaxed">
        We refuse to display fabricated impact numbers. Below is the honest
        picture: who this technology is designed to support, and what we intend
        to measure once real pilots begin.
      </p>
    </div>

    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
      {[
        { t: "Who this is for", b: "People recovering from stroke or mild traumatic brain injury, older adults noticing changes in memory or attention, students building focus habits, and anyone in India who cannot easily afford preventive-health consultation." },
        { t: "What problem they face", b: "Limited access to affordable preventive-health advice and cognitive-rehabilitation support in India, especially in tier-2 and tier-3 cities." },
        { t: "What intervention we provide", b: "A free, private, adaptive platform that measures cognitive-task performance and generates a personalised training plan." },
        { t: "What we measure", b: "Accuracy, reaction time, consistency, session completion, self-reported difficulty — over weeks, not a single test." },
      ].map((c) => (
        <div key={c.t} className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
            {c.t}
          </div>
          <p className="mt-2 text-sm text-zinc-200 leading-relaxed">{c.b}</p>
        </div>
      ))}
    </div>

    <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.03] p-8">
      <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
        Recorded outcomes (empty)
      </div>
      <h3 className="mt-2 font-display text-xl font-semibold text-white">
        No pilot data recorded yet.
      </h3>
      <p className="mt-2 text-sm text-zinc-400 max-w-xl">
        When authorised project team members conduct pilot sessions, aggregated
        (never personally identifiable) outcomes will be added here.
      </p>
      <Link
        to="/neurorehab"
        data-testid="impact-cta-neurorehab"
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-5 py-2.5 text-sm hover:bg-cyan-100"
      >
        Try NeuroRehab AI
        <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  </div>
);
